import { ArticleCardProps } from '@portfolio/models'
import React from 'react'
import { styled } from '@portfolio/styles'
import { SectionQuote } from '../SectionQuote/SectionQuote'
import { SectionIconWithTextList } from '../SectionIconWithTextList/SectionIconWithTextList'
import { SectionSingleMediaWithDescription } from '../SectionSingleMediaWithDescription/SectionSingleMediaWithDescription'
import {
  isRenderableMediaField,
  Link,
  Media,
  PortableText,
} from '@portfolio/components'

const Container = styled('div', {
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px 0',
  color: '$white',
  paddingBottom: '$4',
})

const ArticleShortDescription = styled('p', {
  projectFont: 'body03',
  fontSize: 15,
})

const ArticleLink = styled(Link, {
  projectFont: 'body05',
  textUnderlineOffset: '.35em',
  color: '$white',
})

const MediaWrap = styled('div', {
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '$r2',
  width: '100%',
  flex: '0 0 auto',
  aspectRatio: '1.33',
})

const MediaOverlay = styled('div', {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
})

const LogoWrap = styled('div', {
  position: 'absolute',
  bottom: 20,
  right: 20,
})

const ArticleContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$16',
})

const SectionTextContent = styled('div', {
  projectFont: 'body03',
  fontSize: 15,
})

const SectionListContent = styled('ul', {
  projectFont: 'body03',
  fontSize: 15,
})

export const ArticleCard = ({
  article,
  shortDescription,
  linkLabel,
  media,
  logo,
}: ArticleCardProps) => {
  if (!article) {
    return null
  }

  const mediaContent = isRenderableMediaField(media) ? media : article.thumbnail

  return (
    <Container key={article._key}>
      {mediaContent && (
        <MediaWrap>
          <Media
            {...mediaContent}
            layout="fill"
            objectFit="cover"
            sizes="(min-width: 1024px) 33vw, 100vw"
          />
          <MediaOverlay />
          {logo && (
            <LogoWrap>
              <Media
                {...logo}
                layout="intrinsic"
                disablePlaceholderBackground
              />
            </LogoWrap>
          )}
        </MediaWrap>
      )}
      <ArticleContent>
        {shortDescription ? (
          <ArticleShortDescription>{shortDescription}</ArticleShortDescription>
        ) : (
          article?.introduction && (
            <PortableText
              value={article.introduction}
              disableTopBottomMargins={true}
              additionalComponents={{
                block: {
                  normal: (props) => {
                    return (
                      <SectionTextContent>{props.children}</SectionTextContent>
                    )
                  },
                },
                list: {
                  bullet: (props) => {
                    return (
                      <SectionListContent>{props.children}</SectionListContent>
                    )
                  },
                },
                types: {
                  sectionQuote: (props) => {
                    return <SectionQuote {...props.value} />
                  },
                  sectionIconWithTextList: (props) => {
                    return <SectionIconWithTextList {...props.value} />
                  },
                  sectionSingleMediaWithDescription: (props) => {
                    return (
                      <SectionSingleMediaWithDescription {...props.value} />
                    )
                  },
                },
              }}
            />
          )
        )}
        {article.path && (
          <ArticleLink href={article.path} appearance="textUnderline">
            {linkLabel}
          </ArticleLink>
        )}
      </ArticleContent>
    </Container>
  )
}
